import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, CircularProgress, Typography, useTheme, Divider, alpha } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import * as React from 'react';
import { HFilledTextField } from '../TextField';
import { Icon } from '@iconify/react';
const ModernHCheckboxAutoComplete = React.forwardRef(({ id, options, size = 'medium', disableCloseOnSelect = true, label, placeholder, disabled = false, error, loading = false, bg = 'light', onChange, value: externalValue, getOptionKey, endAdornment, endAdornmentIcon, showDesignation = false, showSelectAll = false, selectAllLabel = 'Select All', selectedCountLabel = 'selected', ...autocompleteProps }, ref) => {
    const theme = useTheme();
    // Add "Select All" option at the beginning if showSelectAll is true
    const optionsWithSelectAll = React.useMemo(() => {
        if (!showSelectAll)
            return options;
        // Check if "Select All" option already exists
        const hasSelectAll = options.some((opt) => opt.value === 'all');
        if (hasSelectAll)
            return options;
        return [{ label: selectAllLabel, value: 'all' }, ...options];
    }, [options, showSelectAll, selectAllLabel]);
    const otherOptions = optionsWithSelectAll.filter((opt) => opt.value !== 'all'); // other options except all
    const selectAllOption = optionsWithSelectAll.find((opt) => opt.value === 'all');
    const mandatoryOptions = React.useMemo(() => otherOptions.filter((opt) => !!opt.isDisabled), [otherOptions]);
    // Helper function to normalize external value and add "Select All" if all items are selected
    const normalizeValue = React.useCallback((value) => {
        if (!value || value.length === 0)
            return [];
        // Filter out any "all" option from external value
        let filteredValue = value.filter((opt) => opt.value !== 'all');
        // Ensure mandatory (disabled) options are always present
        const missingMandatory = mandatoryOptions.filter((m) => !filteredValue.some((v) => v.value === m.value));
        if (missingMandatory.length > 0) {
            filteredValue = [...filteredValue, ...missingMandatory];
        }
        // Calculate other options length (excluding "all")
        const otherOptionsCount = optionsWithSelectAll.filter((opt) => opt.value !== 'all').length;
        // If all options are selected and showSelectAll is true, add "Select All" option
        if (showSelectAll &&
            selectAllOption &&
            filteredValue.length > 0 &&
            filteredValue.length === otherOptionsCount) {
            return [selectAllOption, ...filteredValue];
        }
        return filteredValue;
    }, [showSelectAll, selectAllOption, optionsWithSelectAll, mandatoryOptions]);
    const [selectedOptions, setSelectedOptions] = React.useState(() => normalizeValue(externalValue));
    // Sync with external value prop (guard to avoid infinite re-render loops)
    React.useEffect(() => {
        if (externalValue === undefined)
            return;
        const normalized = normalizeValue(externalValue);
        const sameLength = normalized.length === selectedOptions.length;
        const sameValues = sameLength && normalized.every((opt, idx) => opt.value === selectedOptions[idx]?.value);
        if (!sameValues) {
            setSelectedOptions(normalized);
        }
        // Intentionally omit selectedOptions from deps to avoid redundant runs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalValue, normalizeValue]);
    // Always sort options: selected options at top, then unselected (keep "Select All" at the very top)
    const sortedOptions = React.useMemo(() => {
        const selectedValues = new Set(selectedOptions.map((opt) => opt.value));
        const selected = optionsWithSelectAll.filter((opt) => opt.value !== 'all' && selectedValues.has(opt.value));
        const unselected = optionsWithSelectAll.filter((opt) => opt.value !== 'all' && !selectedValues.has(opt.value));
        // Keep "Select All" at the top, then selected options, then divider, then unselected
        if (selectAllOption) {
            return selected.length > 0 && unselected.length > 0
                ? [selectAllOption, ...selected, { value: '__divider__', label: '__divider__' }, ...unselected]
                : [selectAllOption, ...selected, ...unselected];
        }
        return selected.length > 0 && unselected.length > 0
            ? [...selected, { value: '__divider__', label: '__divider__' }, ...unselected]
            : [...selected, ...unselected];
    }, [optionsWithSelectAll, selectedOptions, selectAllOption]);
    // Helper function to filter out "all" option and only send actual option IDs
    const getFilteredValue = React.useCallback((value) => {
        return value.filter((opt) => opt.value !== 'all');
    }, []);
    const handleChange = (_event, newValue, reason) => {
        // Filter out divider and "all" options
        const filteredNewValue = newValue.filter((opt) => opt.value !== '__divider__' && opt.value !== 'all');
        const isAllInNewValue = newValue.some((opt) => opt.value === 'all');
        const isAllInSelected = selectedOptions.some((opt) => opt.value === 'all');
        const otherValues = filteredNewValue;
        // Case 1: User selects "all" (when it wasn't selected before) - select all options
        if (isAllInNewValue && !isAllInSelected) {
            const allOptionsSelected = selectAllOption
                ? [selectAllOption, ...otherOptions]
                : [...otherOptions];
            setSelectedOptions(allOptionsSelected);
            // Send only actual options (not "all") - all option IDs
            onChange?.(otherOptions);
            return;
        }
        // Case 2: User clicks on already checked "Select All" - deselect all items
        if (isAllInSelected && !isAllInNewValue) {
            // Keep mandatory (disabled) options selected
            const keep = [...mandatoryOptions];
            setSelectedOptions(keep);
            onChange?.(keep);
            return;
        }
        // Case 3: If "all" is selected and user unselects any other option then unselect "all" as well
        if (isAllInSelected && isAllInNewValue && otherOptions.length !== otherValues.length) {
            // Ensure mandatory options remain selected
            const updatedValue = [
                ...otherValues,
                ...mandatoryOptions.filter((m) => !otherValues.some((v) => v.value === m.value))
            ];
            setSelectedOptions(updatedValue);
            // Send only actual options (not "all")
            onChange?.(updatedValue);
            return;
        }
        // Case 4: "all" is not selected and user selects an option which makes all options selected - auto-select "all"
        if (!isAllInSelected &&
            !isAllInNewValue &&
            otherOptions.length === otherValues.length &&
            showSelectAll &&
            selectAllOption) {
            const allOptionsSelected = [selectAllOption, ...otherValues];
            setSelectedOptions(allOptionsSelected);
            // Send only actual options (not "all") - all option IDs
            onChange?.(otherOptions);
            return;
        }
        // Default: Update selected options and check if all are selected to auto-select "Select All"
        // Filter out divider and "all" options
        let finalValue = filteredNewValue;
        // Ensure mandatory options remain selected in all cases
        finalValue = [
            ...finalValue.filter((opt) => opt.value !== 'all' && opt.value !== '__divider__'),
            ...mandatoryOptions.filter((m) => !finalValue.some((v) => v.value === m.value))
        ];
        if (!isAllInNewValue &&
            otherOptions.length === otherValues.length &&
            showSelectAll &&
            selectAllOption) {
            // All options are selected, add "Select All" automatically
            finalValue = [selectAllOption, ...otherOptions];
        }
        setSelectedOptions(finalValue);
        // Always send only actual options (not "all")
        onChange?.(getFilteredValue(finalValue));
    };
    const icon = _jsx(CheckBoxOutlineBlankIcon, { fontSize: 'small' });
    const checkedIcon = _jsx(CheckBoxIcon, { fontSize: 'small' });
    // Custom filter function to search by label, email, designation, and companyName
    const filterOptions = (options, { inputValue }) => {
        if (!inputValue)
            return options;
        const searchLower = inputValue.toLowerCase();
        return options.filter((option) => {
            // Skip divider in filtering
            if (option.value === '__divider__')
                return true;
            const labelMatch = option.label?.toLowerCase().includes(searchLower);
            const emailMatch = option.email?.toLowerCase().includes(searchLower);
            const designationMatch = option.designation?.toLowerCase().includes(searchLower);
            const companyMatch = option.companyName?.toLowerCase().includes(searchLower);
            return labelMatch || emailMatch || designationMatch || companyMatch;
        });
    };
    // Get count of selected items (excluding "Select All")
    const selectedCount = selectedOptions.filter((opt) => opt.value !== 'all').length;
    return (_jsx(Autocomplete, { multiple: true, fullWidth: true, size: size, id: `${id}-modern-checkbox-multiselect`, options: sortedOptions, value: selectedOptions, disabled: disabled, loading: loading, disableCloseOnSelect: disableCloseOnSelect, filterOptions: filterOptions, onChange: (_event, newValue, reason) => handleChange(_event, newValue, reason), isOptionEqualToValue: (o, v) => o.value === v.value, getOptionDisabled: (option) => {
            // Divider is not a selectable option
            if (option.value === '__divider__')
                return true;
            return !!option.isDisabled;
        }, ...(getOptionKey && { getOptionKey }), popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', color: bg === 'blue' ? theme.palette.common.white : theme.palette.grey[700] }), sx: {
            '& .MuiFilledInput-root .MuiAutocomplete-endAdornment': {
                right: '3px'
            }
        }, slotProps: {
            paper: {
                sx: {
                    backgroundColor: '#FFFFFFEB',
                    backdropFilter: 'blur(40px)',
                    color: (theme) => theme.palette.text.primary,
                    boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D',
                    '& .MuiAutocomplete-noOptions': {
                        color: (theme) => theme.palette.text.primary
                    }
                }
            }
        }, renderTags: () => {
            // Don't render any chips, just return null
            return null;
        }, renderOption: (props, option, { selected }) => {
            // Handle divider rendering
            if (option.value === '__divider__') {
                const { key, ...optionProps } = props;
                return (_jsx(Divider, { sx: {
                        my: 1,
                        borderColor: alpha(theme.palette.common.white, 0.1),
                        '&::before, &::after': {
                            borderColor: alpha(theme.palette.common.white, 0.1)
                        }
                    } }, key));
            }
            const { key, ...optionProps } = props;
            const isDisabled = !!option.isDisabled;
            return (_jsx("li", { ...optionProps, style: {
                    ...optionProps.style,
                    opacity: isDisabled ? 0.5 : 1,
                    color: isDisabled ? '#999' : 'inherit',
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                }, children: _jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%'
                    }, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: {
                                marginRight: 8,
                                color: isDisabled ? '#ccc' : 'inherit',
                                alignSelf: showDesignation ? 'flex-start' : 'center',
                                marginTop: showDesignation ? '4px' : 0
                            }, checked: selected, disabled: isDisabled }), _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1
                            }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [option.iconUrl ? (_jsx("img", { src: option.iconUrl, alt: option.label, width: 18, height: 12, style: { borderRadius: 2 } })) : null, _jsx(Typography, { variant: 'body2', sx: {
                                                color: isDisabled ? '#999' : 'inherit',
                                                lineHeight: 1.5
                                            }, children: option.label })] }), (option.email || option.designation || option.companyName) && (_jsxs(Box, { sx: { mt: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }, children: [option.email && (_jsx(Typography, { variant: 'caption', sx: {
                                                color: (theme) => isDisabled
                                                    ? theme.palette.text.disabled
                                                    : theme.palette.common.white,
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }, children: option.email })), (option.designation || option.companyName) && (_jsx(Typography, { variant: 'caption', sx: {
                                                color: (theme) => isDisabled
                                                    ? theme.palette.text.disabled
                                                    : theme.palette.common.white,
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }, children: [option.designation, option.companyName].filter(Boolean).join(' • ') }))] })), showDesignation &&
                                    !option.email &&
                                    !option.designation &&
                                    !option.companyName && (_jsx(Typography, { variant: 'caption', component: 'span', sx: {
                                        color: (theme) => isDisabled ? theme.palette.text.disabled : theme.palette.common.white,
                                        fontSize: '0.75rem',
                                        lineHeight: 1.2,
                                        mt: '2px'
                                    }, children: option.companyName && option.designation ? (_jsxs(_Fragment, { children: [option.companyName, ' ', _jsx(Box, { component: 'span', sx: {
                                                    color: (theme) => theme.palette.primary.main,
                                                    mx: 0.5
                                                }, children: "\u2022" }), ' ', "Designation: ", option.designation] })) : option.companyName ? (option.companyName) : option.designation ? (`Designation: ${option.designation}`) : ('') }))] })] }) }, key));
        }, renderInput: (params) => {
            return (_jsx(HFilledTextField, { ...params, label: label, inputRef: ref, placeholder: selectedCount > 0 ? '' : placeholder, disabled: disabled, error: !!error, helperText: error, bg: bg, inputProps: {
                    ...params.inputProps,
                    value: selectedCount > 0 ? `${selectedCount} ${selectedCountLabel}` : '',
                    readOnly: true
                }, slotProps: {
                    input: {
                        ...params.InputProps,
                        endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { color: 'inherit', size: 20 }), endAdornment, params.InputProps.endAdornment] }))
                    }
                } }));
        }, ...autocompleteProps }));
});
ModernHCheckboxAutoComplete.displayName = 'ModernHCheckboxAutoComplete';
export default ModernHCheckboxAutoComplete;
